import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMapPin, FiCamera, FiCheck, FiLoader } from 'react-icons/fi';
import api from '@/services/api';
import CepInput from '@/components/common/CepInput';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ImageCropper from '@/components/common/ImageCropper';

const BARUERI_CENTER = { lat: -23.5114, lng: -46.8763 };

const EditStore = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [store, setStore] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    address: '', 
    cep: '',
    logo: '' 
  });
  const [location, setLocation] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [imageToCrop, setImageToCrop] = useState(null);

  useEffect(() => {
    loadStore();
  }, []);

  const loadStore = async () => {
    try {
      const response = await api.get('/stores/my');
      const storeData = response.data;
      setStore(storeData);
      setFormData({
        name: storeData.name || '',
        address: storeData.address || '',
        cep: storeData.cep || '',
        logo: storeData.logo || ''
      });
      if (storeData.coordinates) {
        setLocation(storeData.coordinates);
      }
    } catch (err) {
      setError('Erro ao carregar dados da loja');
    } finally {
      setLoading(false);
    }
  };

  const handleAddressFound = (addressData) => {
    setFormData(prev => ({
      ...prev,
      address: addressData.fullAddress,
      cep: addressData.cep
    }));
    if (!location) {
      setLocation(BARUERI_CENTER);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Imagem muito grande. Máximo 5MB.');
      return;
    }

    // Create URL for cropper
    const imageUrl = URL.createObjectURL(file);
    setImageToCrop(imageUrl);
    setError('');
  };

  const handleCropComplete = async (croppedBlob) => {
    setImageToCrop(null);
    setUploading(true);
    setError('');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', croppedBlob, 'logo.jpg');
      
      const response = await api.post('/upload/image', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, logo: response.data.url }));
      setSuccess('Logo atualizado! Clique em Salvar para confirmar.');
    } catch (err) {
      setError('Erro ao fazer upload da imagem');
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    setImageToCrop(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Nome da loja é obrigatório');
      return;
    }

    if (!formData.address.trim()) {
      setError('Endereço é obrigatório');
      return;
    }

    setSaving(true);

    try {
      const updateData = {
        name: formData.name,
        address: formData.address,
        logo: formData.logo || null
      };

      if (location) {
        updateData.coordinates = location;
      }

      await api.put(`/stores/${store.id}`, updateData);
      setSuccess('Loja atualizada com sucesso!');
      
      // Reload store data
      await loadStore();
      
      setTimeout(() => {
        navigate('/lojista');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar loja');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Nenhuma loja encontrada</p>
          <button
            onClick={() => navigate('/lojista/loja/criar')}
            className="px-6 py-2 bg-[#DC143C] text-white rounded-lg"
          >
            Criar Loja
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Image Cropper Modal */}
      {imageToCrop && (
        <ImageCropper
          image={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/lojista')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiArrowLeft className="text-xl" />
          </button>
          <h1 className="font-semibold text-gray-800">Editar Loja</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Logo */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="font-semibold text-gray-800 mb-4">Logo da Loja</h2>
            
            <div className="flex items-center gap-6">
              {/* Preview */}
              <div className="relative">
                {formData.logo ? (
                  <img
                    src={formData.logo}
                    alt="Logo"
                    className="w-24 h-24 rounded-xl object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                    <span className="text-3xl font-bold text-gray-400">
                      {formData.name?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                )}
                
                {uploading && (
                  <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                    <FiLoader className="text-white text-2xl animate-spin" />
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div className="flex-1">
                <label className="block">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors">
                    <FiCamera />
                    <span>{formData.logo ? 'Trocar foto' : 'Adicionar foto'}</span>
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoSelect}
                    className="hidden"
                  />
                </label>
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium mb-2">📸 Como funciona:</p>
                  <ul className="text-xs text-amber-700 space-y-1">
                    <li>1. Selecione uma imagem</li>
                    <li>2. Arraste para centralizar o logo</li>
                    <li>3. Use o zoom para ajustar</li>
                    <li>4. Confirme para salvar</li>
                  </ul>
                </div>
                <div className="mt-2 text-xs text-gray-500 space-y-1">
                  <p>📐 <strong>Tamanho recomendado:</strong> 200x200 pixels</p>
                  <p>📁 <strong>Formatos:</strong> JPG, PNG ou WebP</p>
                  <p>📦 <strong>Tamanho máximo:</strong> 5MB</p>
                </div>
              </div>
            </div>
          </div>

          {/* Store Info */}
          <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
            <h2 className="font-semibold text-gray-800">Informações da Loja</h2>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">Nome da Loja *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: TechStore Barueri"
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">CEP</label>
              <CepInput
                value={formData.cep}
                onChange={(value) => setFormData(prev => ({ ...prev, cep: value }))}
                onAddressFound={handleAddressFound}
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Endereço Completo *</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Rua, número, bairro - Cidade"
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none"
                required
              />
            </div>

            {location && (
              <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                <FiMapPin />
                <span>Localização definida</span>
              </div>
            )}
          </div>

          {/* Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-2">
              <FiCheck />
              {success}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={saving}
            className="w-full py-4 bg-[#DC143C] text-white rounded-xl font-semibold hover:bg-[#B01030] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <FiLoader className="animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <FiCheck />
                Salvar Alterações
              </>
            )}
          </button>
        </form>
      </main>
    </div>
  );
};

export default EditStore;
